import {
  Company,
  isServiceTitanApiPhoneCall,
  isServiceTitanPortalV2PhoneCall,
  PhoneCallDetailDTO,
  SupportedProviderEnum,
  TimezoneAbbreviationEnum,
  UserDetailDTO
} from '_lib/api/apiTypes.shim';

import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';

import { appRoutePaths, getFullAppRoutePath } from '../Nav/nav.types';

// This list of CSV headers is ordered :)
const csvColumns = [
  'companyName',
  'companyDNIS',
  'companyId',
  'customerDisplayName',
  'callerId',
  'createdByName',
  'createdById',
  'isManualCall',
  'outcome',
  'duration',
  'appointmentDate',
  'previousAppointmentDate',
  'previousAppointmentDateTime',
  'notes',
  'internalNotes',
  'sendEscalationEmail',
  'sendCompanyEmail',
  'toCompanyEmailsCsv',
  'toCompanyCCEmailsCsv',
  'toCompanyBCCEmailsCsv',
  'toCompanyPlainTextEmailsCsv',
  'createdAt',
  'providerType',
  'companyIsZoned',
  'zoneName',
  'zoneId',
  'id',
  'originalCompany',
  'jobNo',
  'fullDetails',
  'stJobSummary',
  'stJobHistoryNotes',
  'stAppointmentNotes',
  'stJobCancelReason',
  'stJobCancelNotes'
] as const;

type CsvRow = { [K in (typeof csvColumns)[number]]: string };

const headerRow: CsvRow = {
  companyName: 'Company',
  companyDNIS: 'Company DNIS',
  companyId: 'Company ID',
  customerDisplayName: 'Customer',
  callerId: 'Caller ID',
  createdByName: 'Call Agent',
  createdById: 'Call Agent ID',
  isManualCall: 'Manually created?',
  outcome: 'Outcome',
  duration: 'Call Duration',
  appointmentDate: 'Appointment Date',
  previousAppointmentDate: 'Previous Appointment Date',
  previousAppointmentDateTime: 'Previous Appointment Date Time',
  notes: 'Notes',
  internalNotes: 'Internal Notes',
  sendEscalationEmail: 'Escalation Email Sent',
  sendCompanyEmail: 'Company Email Sent',
  toCompanyEmailsCsv: 'To Email',
  toCompanyCCEmailsCsv: 'CC Email',
  toCompanyBCCEmailsCsv: 'BCC Email',
  toCompanyPlainTextEmailsCsv: 'To Plain Text Email',
  createdAt: 'Created Date',
  providerType: 'Provider Type',
  companyIsZoned: 'Zoned',
  zoneName: 'Zone Name',
  zoneId: 'Zone ID',
  id: 'ID',
  originalCompany: 'Originated From',
  jobNo: 'Job Number',
  fullDetails: 'Full Details',
  stJobSummary: 'ST Job Summary',
  stJobHistoryNotes: 'ST Job History Notes',
  stAppointmentNotes: 'ST Appointment Notes',
  stJobCancelReason: 'ST Job Cancel Reason',
  stJobCancelNotes: 'ST Job Cancel Notes'
};

const mapPhoneCallToCsvRow = ({
  phoneCall,
  timezone,
  company,
  user
}: {
  phoneCall: PhoneCallDetailDTO;
  timezone: TimezoneAbbreviationEnum;
  company?: Company;
  user?: Pick<UserDetailDTO, 'firstName' | 'lastName'>;
}) => {
  const csvRow: CsvRow = {
    companyName: '',
    companyDNIS: '',
    companyId: '',
    customerDisplayName: '',
    callerId: '',
    createdByName: '',
    createdById: '',
    isManualCall: '',
    outcome: '',
    duration: '',
    appointmentDate: '',
    previousAppointmentDate: '',
    previousAppointmentDateTime: '',
    notes: '',
    internalNotes: '',
    sendEscalationEmail: '',
    sendCompanyEmail: '',
    toCompanyEmailsCsv: '',
    toCompanyCCEmailsCsv: '',
    toCompanyBCCEmailsCsv: '',
    toCompanyPlainTextEmailsCsv: '',
    createdAt: '',
    providerType: '',
    companyIsZoned: '',
    zoneName: '',
    zoneId: '',
    id: '',
    originalCompany: '',
    jobNo: '',
    fullDetails: '',
    stJobSummary: '',
    stJobHistoryNotes: '',
    stAppointmentNotes: '',
    stJobCancelReason: '',
    stJobCancelNotes: ''
  };

  csvRow.companyName = company?.name || 'Inactive Company';
  csvRow.companyDNIS = phoneCall.companyDNIS;
  csvRow.companyId = phoneCall.companyId;
  csvRow.customerDisplayName = phoneCall.customerDisplayName || '';
  csvRow.callerId = phoneCall.callerId;
  csvRow.createdByName = !user ? 'Unknown Call Agent' : `${user.firstName} ${user.lastName}`;
  csvRow.createdById = phoneCall.createdById;
  csvRow.isManualCall = phoneCall.isManualCall ? 'Yes' : 'No';
  csvRow.outcome = phoneCall.outcome;
  csvRow.duration = DateUtils.toPrettyDuration(phoneCall.duration);
  csvRow.appointmentDate = phoneCall.appointmentDate
    ? DateUtils.format(phoneCall.appointmentDate, DateFormatEnum.DateTimeWeekdayShort, { timezone })
    : '';
  csvRow.previousAppointmentDate = phoneCall.previousAppointmentDate
    ? DateUtils.format(phoneCall.previousAppointmentDate, DateFormatEnum.DateTimeWeekdayShort, {
        timezone
      })
    : '';
  csvRow.previousAppointmentDateTime = phoneCall.previousAppointmentDateTime
    ? DateUtils.format(phoneCall.previousAppointmentDateTime, DateFormatEnum.DateTimeWeekdayShort, {
        timezone
      })
    : '';
  csvRow.notes = phoneCall?.notes?.trim() || '';
  csvRow.internalNotes = phoneCall?.internalNotes?.trim() || '';
  csvRow.sendEscalationEmail = phoneCall.sendEscalationEmail ? 'Yes' : 'No';
  csvRow.sendCompanyEmail = phoneCall.sendCompanyEmail ? 'Yes' : 'No';
  csvRow.toCompanyEmailsCsv = phoneCall.toCompanyEmails ? phoneCall.toCompanyEmails.join(', ') : '';
  csvRow.toCompanyCCEmailsCsv = phoneCall.toCompanyCCEmails ? phoneCall.toCompanyCCEmails.join(', ') : '';
  csvRow.toCompanyBCCEmailsCsv = phoneCall.toCompanyBCCEmails ? phoneCall.toCompanyBCCEmails.join(', ') : '';
  csvRow.toCompanyPlainTextEmailsCsv = phoneCall.toCompanyPlainTextEmails
    ? phoneCall.toCompanyPlainTextEmails.join(', ')
    : '';
  csvRow.createdAt = DateUtils.format(phoneCall.createdAt, DateFormatEnum.DateTimeWeekdayShort, {
    timezone
  });
  csvRow.providerType = phoneCall.providerType;
  csvRow.companyIsZoned = phoneCall.companyIsZoned ? 'Yes' : 'No';
  // company wasn't zoned at the time of the call
  if (!phoneCall.companyIsZoned) {
    csvRow.zoneName = 'No Zone';
  }
  // Company was zoned, but phone call didn't record the zone
  else if (!phoneCall.companyZoneId) {
    csvRow.zoneName = 'No Zone Selected';
  } else {
    const zone = (company?.zones || []).find((z) => z.id === phoneCall.companyZoneId);
    csvRow.zoneName = phoneCall.companyZoneTitle || zone?.title || 'Unknown Zone Title';
  }
  csvRow.zoneId = phoneCall.companyZoneId || 'none';
  csvRow.id = phoneCall.id;
  csvRow.originalCompany = phoneCall.originalCompany || '';

  csvRow.fullDetails = `${window.location.origin}${getFullAppRoutePath(appRoutePaths.CallLog)}/${phoneCall.id}`;

  if (phoneCall.providerType === SupportedProviderEnum.SWAPI) {
    csvRow.jobNo = phoneCall.job?.jobNo || '';
  } else if (isServiceTitanApiPhoneCall(phoneCall)) {
    csvRow.jobNo = phoneCall?.providerData?.job?.jobNo || '';
    csvRow.stJobCancelReason = phoneCall?.providerData?.jobCancelReason?.name || '';
  } else if (isServiceTitanPortalV2PhoneCall(phoneCall)) {
    csvRow.stJobSummary = phoneCall?.providerData?.job?.notes?.trim() || '';
    csvRow.stJobHistoryNotes = phoneCall?.providerData?.jobHistoryNotes?.length
      ? phoneCall?.providerData.jobHistoryNotes
          .sort((n1, n2) => (n1.modifiedAt > n2.modifiedAt ? -1 : 1)) // sort descending modified at
          .map((note) => `(${note.modifiedAt}): ${note.text.trim()}`)
          .join('\n')
      : '';
    csvRow.stAppointmentNotes = phoneCall?.providerData?.appointment?.specialInstructions?.trim() || '';
    csvRow.stJobCancelReason = phoneCall?.providerData?.jobCancelEvent?.name || '';
    csvRow.stJobCancelNotes = phoneCall?.providerData?.jobCancelEvent?.notes?.trim() || '';
  }

  return csvRow;
};

export const generatePhoneCallsCsv = ({
  phoneCalls,
  timezone,
  companies,
  users
}: {
  phoneCalls: PhoneCallDetailDTO[];
  timezone: TimezoneAbbreviationEnum;
  companies: Company[];
  users?: Pick<UserDetailDTO, 'id' | 'firstName' | 'lastName'>[];
}): string => {
  const rowObjectArray = [headerRow];
  const mappedPhoneCalls = phoneCalls.map((phoneCall) =>
    mapPhoneCallToCsvRow({
      phoneCall,
      timezone,
      company: companies.find((company) => company.id === phoneCall.companyId),
      user: users?.find((user) => user.id === phoneCall.createdById)
    })
  );
  rowObjectArray.push(...mappedPhoneCalls);

  const csvData = rowObjectArray.reduce<string[][]>((acc, row) => {
    const orderedRow: string[] = [];
    // Use ordered constant array to build 2D array using object keys
    csvColumns.forEach((columnName) => {
      const value = row[columnName];
      orderedRow.push(value);
    });
    acc.push(orderedRow);
    return acc;
  }, []);

  // see https://stackoverflow.com/a/68146412
  const csvString = csvData
    .map(
      (row) =>
        row
          .map((v) =>
            v.replace(/[\b\f\n\r\t\v\0'"\\]/g, (match) => {
              return (
                {
                  '\b': '\\b',
                  '\f': '\\f',
                  '\n': '\\n',
                  '\r': '\\r',
                  '\t': '\\t',
                  '\v': '\\v',
                  '\0': '\\0',
                  "'": "\\'",
                  '"': '\\"',
                  '\\': '\\\\'
                }[match] || ''
              );
            })
          )
          .map((v) => `"${v}"`) // quote it
          .join(',') // comma-separated
    )
    .join('\r\n'); // rows starting on new lines

  return csvString;
};

export const downloadCsv = (csvString: string, filename: string = 'call-log.csv'): void => {
  const blob = new Blob([csvString], { type: 'data:text/csv;charset=utf-8' });

  const csvFileLink = document.createElement('a');

  const urlCreator = window.URL || window.webkitURL;
  const url = urlCreator.createObjectURL(blob);

  csvFileLink.setAttribute('href', url);
  csvFileLink.setAttribute('download', filename);
  csvFileLink.style.visibility = 'hidden';

  document.body.appendChild(csvFileLink);
  csvFileLink.click();
  document.body.removeChild(csvFileLink);
};
